import { BTN_TEXT_ENABLED, BTN_TEXT_LOADING } from 'constants.js'
import { PayloadAction, PayloadActionCreator, createSlice } from '@reduxjs/toolkit'
import { fetchPaymentTokenReq, patchRedeemCodeReq, postNewTransactionReq } from 'api/api'
import * as safeApi from 'api'
import { flatMap, get, pick, reduce } from 'lodash'
import { getChosenPassTier, getFormValues, chosenPassTierHasSoldOut } from './passTier'
import { getShow, passTierHasSoldOut, showHasSoldOut } from './liveShow'
import { showPassListReceived, purchasePassReceieved, getShowPassList } from 'redux/slices/passes'

import { createFetcherThunk } from 'redux/thunkCreators'
import { createSelector } from 'reselect'
import { retrieveUserCustomLuas } from './liveUserActions/thunks'
import * as Result from 'data/ApiResult'
import { AppThunk, RootState } from 'redux/store'
import {
    onUserPurchasedPassMsg,
    onConfirmationCodeRedeemedMsg,
    onShowSoldOutMsg,
    onUserAccessPrivateEvent,
} from './messagingBanner/thunks'
import { isPopulatedArray } from 'utils/populated'
import ReactPixel from 'react-facebook-pixel'
import ReactGA from 'react-ga'
import { Pass } from 'data/Pass'
import { ScopeType } from 'data/Show'
import { getCallbacks as getAuth0Callbacks } from 'libs/web-client/utils/auth0/src'

export enum PaywallDisplay {
    PROMPT = 'PROMPT',
    PAYMENT = 'PAYMENT',
    CHOOSE_PASS_TIER = 'CHOOSE_PASS_TIER',
    SUCCESS_TOAST = 'SUCCESS_TOAST',
    PURCHASE_SUCCESS_SIGNUP = 'PURCHASE_SUCCESS_SIGNUP',
    SUCCESS_CONFIRMATION = 'SUCCESS_CONFIRMATION',
    SUCCESS_REDEMPTION = 'SUCCESS_REDEMPTION',
    CODE_REDEMPTION = 'CODE_REDEMPTION',
    FREE_TIER_EMAIL = 'FREE_TIER_EMAIL',
    ACCESS = 'ACCESS',
    REDEEM_ONLY = 'REDEEM_ONLY',
}

// TODO: remove this after adding the corresponding types to the values below
interface AnyObj {
    [k: string]: any
}

interface PaywallState {
    lastRedeemedCode: string | null
    paymentInstance: AnyObj // TODO: add the corresponding types
    displayState: PaywallDisplay
    alertType: string
    showAlert: boolean
    alertMsg: string
    disableSubmit: boolean
    submitBtnText: string
    isRedeemed: boolean
    redemptionError: string | null
    shouldClearSelectedPayment: boolean
    paymentToken: string | null
}

export const initialState: PaywallState = {
    lastRedeemedCode: null,
    paymentInstance: {},
    displayState: PaywallDisplay.PROMPT,
    alertType: 'danger',
    showAlert: false,
    alertMsg: '',
    disableSubmit: true,
    submitBtnText: BTN_TEXT_LOADING,
    isRedeemed: false,
    redemptionError: null,
    shouldClearSelectedPayment: false,
    paymentToken: null,
}

const paywallSlice = createSlice({
    name: 'paywall',
    initialState,
    reducers: {
        resetState: _ => initialState,
        paymentTokenReceived: (state, { payload }) => ({ ...state, paymentToken: payload }),
        provideAccess: state => {
            state.displayState = PaywallDisplay.ACCESS
        },
        setDisplayState: (state, { payload: displayState }: PayloadAction<PaywallDisplay>) => {
            state.displayState = displayState
        },
        setRedeemedCode: (state, { payload }: PayloadAction<Pass>) => {
            state.lastRedeemedCode = payload.redemptionCode
        },
        updateAlertAndBtnState: (state, { payload }) => ({ ...state, ...payload }),
        setIsRedeemed: state => {
            state.isRedeemed = true
        },
        setRedemptionError: (state, { payload }) => {
            state.redemptionError = payload.error
        },
        setShouldClearSelectedPayment: (state, { payload }) => ({ ...state, shouldClearSelectedPayment: payload }),
        setAuthError: (state, { payload }) => ({ ...state, authError: payload }),
    },
})

const { actions, reducer } = paywallSlice
export default reducer

export const getPaywall = store => store.paywall
export const getRedemptionCode = createSelector(getPaywall, paywall => paywall.lastRedeemedCode)
export const getDisplayState = createSelector(getPaywall, paywall => paywall.displayState)
export const getPaymentToken = createSelector(getPaywall, paywall => paywall.paymentToken)
export const getAlertType = createSelector(getPaywall, paywall => paywall.alertType)
export const getAlertState = createSelector(getPaywall, paywall => paywall.showAlert)
export const getAlertMsg = createSelector(getPaywall, paywall => paywall.alertMsg)
export const getSubmitBtnState = createSelector(getPaywall, paywall => paywall.disableSubmit)
export const getSubmitBtnTxt = createSelector(getPaywall, paywall => paywall.submitBtnText)
export const getRedemptionError = createSelector(getPaywall, paywall => paywall.redemptionError)
export const getAuthError = createSelector(getPaywall, paywall => paywall.authError)
export const isRedeemed = createSelector(getPaywall, paywall => paywall.isRedeemed)
export const shouldClearSelectedPayment = createSelector(getPaywall, paywall => paywall.shouldClearSelectedPayment)

export const getTierFormValues = (state: RootState) => {
    const chosenPassTier = getChosenPassTier(state)
    const fieldsIds = flatMap(chosenPassTier?.formGroups, group => group.fields.map(field => field._id))
    const formValues = getFormValues(state)
    const passTierFormValues = pick(formValues, fieldsIds)

    return reduce(passTierFormValues, (result, value, _id) => [...result, { _id, value }], [])
}

export const setDisplayState = actions.setDisplayState as PayloadActionCreator<PaywallDisplay>
export const {
    resetState,
    paymentTokenReceived,
    setRedeemedCode,
    updateAlertAndBtnState,
    setRedemptionError,
    setIsRedeemed,
    setShouldClearSelectedPayment,
    setAuthError,
    provideAccess,
} = actions

export const setInitialPaywallState = showId => async (dispatch, getState) => {
    const show = getShow(getState())!
    const passes = getShowPassList(getState())

    if (passes.some(pass => pass.show === showId)) {
        dispatch(setDisplayState(PaywallDisplay.ACCESS))

        if (show.scope === ScopeType.PUBLIC) {
            dispatch(onUserPurchasedPassMsg())
        } else {
            dispatch(onUserAccessPrivateEvent())
        }
    } else if (show?.disablePaywall) {
        dispatch(setDisplayState(PaywallDisplay.REDEEM_ONLY))
    } else {
        dispatch(setDisplayState(PaywallDisplay.PROMPT))
    }
}

export const retrievePaymentToken = createFetcherThunk(fetchPaymentTokenReq, null, paymentTokenReceived)

export const successfulPurchaseCallback = () => async dispatch => {
    const { isAuthenticated } = getAuth0Callbacks()

    dispatch(
        setDisplayWithToast(
            isAuthenticated() ? PaywallDisplay.SUCCESS_CONFIRMATION : PaywallDisplay.PURCHASE_SUCCESS_SIGNUP,
        ),
    )

    dispatch(onUserPurchasedPassMsg())
}

export const purchaseNewPass = data => async (dispatch, getState: () => RootState) => {
    try {
        const clearSelectedPayment = shouldClearSelectedPayment(getState())

        if (clearSelectedPayment) {
            dispatch(setShouldClearSelectedPayment(false))
        }

        const pass = await postNewTransactionReq({
            ...data,
            tierFormValues: getTierFormValues(getState()),
        })

        const show = getShow(getState())!

        dispatch(purchasePassReceieved(pass.pass))
        dispatch(setRedeemedCode(pass.pass))

        if (isPopulatedArray(show.luaPassTiers)) {
            dispatch(retrieveUserCustomLuas(show.luaPassTiers, show._id))
        }

        // @ts-ignore
        const passTier = getChosenPassTier(getState())
        const amount = passTier?.serviceFee ? passTier.amount + passTier.serviceFee : passTier?.amount
        window.heap.track('Purchase Success')
        ReactPixel.track('Purchase', {
            value: amount,
            currency: 'USD',
        })
        ReactGA.event({ category: 'Pass Purchase', action: 'Form Submission', label: 'Purchase Success' })
        dispatch(successfulPurchaseCallback())
    } catch (err) {
        dispatch(handlePassPurchaseError(err))
    }
}

const handlePassPurchaseError = (err: any): AppThunk => async dispatch => {
    const status = get(err, 'response.data.statusCode')
    const errors = get(err, 'response.data.errors')
    const msg = get(err, 'response.data.msg')

    let alertMsg =
        "Error encountered, please try again. If the problem persists please contact us. We'll do our best to make things right."

    let disableSubmit = false

    switch (status) {
        case 400:
        case 403: {
            alertMsg = msg
            disableSubmit = true
            dispatch(setShouldClearSelectedPayment(true))
            break
        }
        case 410: {
            alertMsg = msg
            disableSubmit = true

            if (!errors) break

            if (errors.type === 'pass-tier' && errors.passTierId) {
                dispatch(passTierHasSoldOut(errors.passTierId))
            }

            if (errors.type === 'show-sold-out') {
                dispatch(showHasSoldOut())
                dispatch(onShowSoldOutMsg())
            }

            dispatch(chosenPassTierHasSoldOut())

            break
        }
    }

    dispatch(updateAlertAndBtnState({ disableSubmit, submitBtnText: BTN_TEXT_ENABLED, alertMsg, showAlert: true }))
}

export const redeemPass = code => async (dispatch, getState: () => RootState) => {
    if (!code) {
        return dispatch(setRedemptionError({ error: 'You must provide a confirmation code' }))
    }

    try {
        const show = getShow(getState())!

        await patchRedeemCodeReq(show._id, code)
        dispatch(setIsRedeemed())
        dispatch(onConfirmationCodeRedeemedMsg())
        dispatch(setDisplayWithToast(PaywallDisplay.SUCCESS_REDEMPTION))

        const result = await safeApi.getAllPasses({ params: { showId: show._id } })

        Result.cata(result, {
            Success: data => dispatch(showPassListReceived(data.passes)),
            HttpError: error => {
                throw error
            },
            _: console.error,
        })

        if (isPopulatedArray(show.luaPassTiers)) {
            dispatch(retrieveUserCustomLuas(show.luaPassTiers, show._id))
        }

        window.heap.track('Code Successfully Redeemed')
    } catch (err) {
        dispatch(setRedemptionError({ error: get(err, 'response.data.msg') }))
    }
}

export const setDisplayWithToast = (displayState: PaywallDisplay) => dispatch => {
    dispatch(setDisplayState(PaywallDisplay.SUCCESS_TOAST))
    setTimeout(() => {
        dispatch(setDisplayState(displayState))
    }, 2000)
}

export const passPurchaseSuccess = (): AppThunk => async dispatch => {
    const { isAuthenticated } = getAuth0Callbacks()

    dispatch(
        isAuthenticated()
            ? setDisplayWithToast(PaywallDisplay.SUCCESS_CONFIRMATION)
            : setDisplayWithToast(PaywallDisplay.PURCHASE_SUCCESS_SIGNUP),
    )
}

export const setAlertAndBtnState = (disableSubmit, submitBtnText, showAlert, alertMsg) =>
    updateAlertAndBtnState({ disableSubmit, submitBtnText, showAlert, alertMsg })

export const submitFreePass = (email: string): AppThunk => async (dispatch, getState) => {
    const show = getShow(getState())
    const selectedPassTier = getChosenPassTier(getState())

    const result = await safeApi.postFreePass({
        email,
        showId: show!._id,
        passTierId: selectedPassTier!._id,
        tierFormValues: getTierFormValues(getState()),
    })

    Result.cata(result, {
        Success: pass => {
            dispatch(purchasePassReceieved(pass))
            dispatch(setRedeemedCode(pass))
            dispatch(passPurchaseSuccess())
            dispatch(onUserPurchasedPassMsg())
            window.heap.track('Free Pass Purchase Success')
        },
        HttpError: err => {
            dispatch(handlePassPurchaseError(err))
        },
        _: () => {},
    })
}
